import { Point, Wall, FloorPlanElement, BoilerElement } from '../types';
import { distanceToLine } from './floorPlanUtils';
import { SNAP_THRESHOLD, WALL_THICKNESS, BOILER_WALL_OFFSET } from '../constants/floorPlanConstants';

function getClosestPointOnSegment(point: Point, start: Point, end: Point): Point {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (dx === 0 && dy === 0) return start;
    const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy);
    const clampedT = Math.max(0, Math.min(1, t));
    return { x: start.x + clampedT * dx, y: start.y + clampedT * dy };
}

export function findWallForBoiler(point: Point, walls: (Wall & { tempId: string, stableId: string })[]): (Wall & { tempId: string, stableId: string }) | null {
    let clickedWall: (Wall & { tempId: string, stableId: string }) | null = null;
    let minDistance = Infinity;

    for (const wall of walls) {
        const distance = distanceToLine(point, wall.start, wall.end);
        if (distance < SNAP_THRESHOLD) {
            if (distance < minDistance) {
                minDistance = distance;
                clickedWall = wall;
            }
        }
    }
    return clickedWall;
}

export function createBoilerElement(
    point: Point,
    wall: Wall & { tempId:string, stableId: string },
    currentFloor: number
): BoilerElement | null {
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);

    const closestPointOnWall = getClosestPointOnSegment(point, wall.start, wall.end);
    const distFromStart = Math.hypot(closestPointOnWall.x - wall.start.x, closestPointOnWall.y - wall.start.y);

    let positionOnWall = distFromStart / wallLength;

    const boilerCenterOnWall = {
        x: wall.start.x + (wall.end.x - wall.start.x) * positionOnWall,
        y: wall.start.y + (wall.end.y - wall.start.y) * positionOnWall,
    };

    const wallDx = wall.end.x - wall.start.x;
    const wallDy = wall.end.y - wall.start.y;
    
    const wallAngle = Math.atan2(wallDy, wallDx);
    const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };

    const crossProduct = wallDx * (point.y - wall.start.y) - wallDy * (point.x - wall.start.x);
    const side = crossProduct > 0 ? -1 : 1;

    const totalOffset = (WALL_THICKNESS / 2 + BOILER_WALL_OFFSET) * side;

    const boilerCenter = {
        x: boilerCenterOnWall.x + perpVector.x * totalOffset,
        y: boilerCenterOnWall.y + perpVector.y * totalOffset,
    };
    
    const newBoiler: BoilerElement = {
        id: `boiler-preview-${Date.now()}`,
        type: 'boiler',
        wallId: wall.stableId,
        positionOnWall,
        floor: currentFloor,
        position: boilerCenter,
        rotation: wallAngle,
        side: side,
        heatingType: 'combi',
        brand: '',
        model: '',
        gcNumber: '',
    };
    
    return newBoiler;
}

// Helper to draw a rounded rectangle path (for pill-like badges)
function drawRoundedRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
}

function drawSingleBoiler(ctx: CanvasRenderingContext2D, boiler: BoilerElement, viewOffset: Point, scale: number, isEstate: boolean = false) {
    const { position, rotation } = boiler;

    // Use position directly since canvas context is already transformed
    ctx.save();
    ctx.translate(position.x, position.y);
    ctx.rotate(rotation);

    const text = 'H/S';
    
    const baseFontSize = 12; // smaller base to reduce pill footprint
    const scaledFontSize = Math.max(7 / scale, Math.min(16 / scale, baseFontSize / scale));

    // Set font before measuring to get accurate text width
    ctx.font = `bold ${scaledFontSize}px sans-serif`;

    // Draw yellow rounded pill background
    const paddingX = Math.max(1 / scale, scaledFontSize * 0.25);
    const paddingY = Math.max(0.5 / scale, scaledFontSize * 0.2);
    const textWidth = ctx.measureText(text).width || (text.length * scaledFontSize * 0.6);
    const boxWidth = textWidth + paddingX * 2;
    const boxHeight = scaledFontSize + paddingY * 2;
    const corner = Math.min(boxHeight / 2, 6 / scale);

    // 🏢 ESTATE MODE: Use white background for professional B&W look
    ctx.fillStyle = isEstate ? '#ffffff' : '#fde047'; // white for estate, yellow for normal
    drawRoundedRectPath(ctx, -boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, corner);
    ctx.fill();
    
    // Draw black border
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1 / scale;
    drawRoundedRectPath(ctx, -boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, corner);
    ctx.stroke();

    // Draw black text
    ctx.fillStyle = '#000000'; // black text
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, 0);

    ctx.restore();
}

export function drawBoilers(
    ctx: CanvasRenderingContext2D,
    elements: FloorPlanElement[],
    currentFloor: number,
    scale: number,
    viewOffset: Point,
    isEstate: boolean = false
) {
    const boilers = elements.filter((e: any) => e.type === 'boiler' && e.floor === currentFloor) as BoilerElement[];

    for (const boiler of boilers) {
        drawSingleBoiler(ctx, boiler, viewOffset, scale, isEstate);
    }
}

export function drawBoilerPreview(
    ctx: CanvasRenderingContext2D,
    boilerPreview: BoilerElement | null,
    scale: number,
    viewOffset: Point,
    isEstate: boolean = false
) {
    if (!boilerPreview) return;

    ctx.save();
    ctx.globalAlpha = 0.7;
    drawSingleBoiler(ctx, boilerPreview, viewOffset, scale, isEstate);
    ctx.restore();
} 